import { CornerDownRight } from 'react-feather';
import { Trans } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';
import { PendingLabel } from './StatusLabels';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import usePaymentMethods from '../../../../common/usePaymentMethods';

type Payment = GetOrder['order']['payments']['data'][0];

export interface PaymentDetailsProps {
  payment: Payment;
}

const PaymentDetails = ({ payment }: PaymentDetailsProps) => {
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const { getPaymentMethodImageUrl, getPaymentMethodTitle } = usePaymentMethods();

  const imageUrl = getPaymentMethodImageUrl(payment.payment_method || 'creditcard');

  const refunds = payment.refunds.data.filter(
    (refund) => refund.status === 'refunded' || refund.status === 'pending' || refund.status === 'processing',
  );

  const paymentMethodTitle = payment.payment_method && getPaymentMethodTitle(payment.payment_method);

  // The payment date is equal to the moment that the payment status changed to 'paid'.
  // The refund date is equal to the moment that the refund was initiated.
  return (
    <>
      <UI.FlexContainer sc={{ alignItems: 'center' }}>
        <UI.Icon sc={{ mr: 3 }}>
          <img
            src={imageUrl}
            alt={paymentMethodTitle}
            title={paymentMethodTitle}
            style={{ width: 32, height: 24, display: 'block' }}
          />
        </UI.Icon>

        <UI.Div sc={{ color: 'gray.400' }}>
          <Trans
            i18nKey="amount_paid_on_date"
            values={{
              amount: formatCurrency(payment.amount),
              date: formatDate(
                parseDate(payment.status_changed_at, { timezone: 'UTC' }),
                { format: 'display_compact_date_time' },
              ),
            }}
          >
            <UI.Strong sc={{ color: 'gray.800' }} />
          </Trans>
          {payment.consumer_account && ` (${payment.consumer_account})`}
        </UI.Div>
      </UI.FlexContainer>

      {refunds.map((refund) => (
        <UI.FlexContainer key={refund.id} sc={{ alignItems: 'center' }}>
          <UI.Span
            sc={{ mr: 3, flex: { alignItems: 'center', justifyContent: 'center' } }}
            style={{ width: 32, height: 24 }}
          >
            <UI.Icon sc={{ color: 'gray.400' }}>
              <CornerDownRight />
            </UI.Icon>
          </UI.Span>
          <UI.Div sc={{ inlineBlock: true }}>
            <UI.Span sc={{ color: 'gray.400' }}>
              <Trans
                i18nKey="amount_refunded_on_date"
                values={{
                  amount: formatCurrency(refund.amount),
                  date: formatDate(
                    parseDate(refund.created_at, { timezone: 'UTC' }),
                    { format: 'display_compact_date_time' },
                  ),
                }}
              >
                <UI.Span sc={{ color: 'gray.800' }} />
              </Trans>
            </UI.Span>

            {(refund.status === 'pending' || refund.status === 'processing') && (
              <UI.Div sc={{ inlineBlock: true, ml: 2 }}>
                <PendingLabel />
              </UI.Div>
            )}
          </UI.Div>
        </UI.FlexContainer>
      ))}
    </>
  );
};

export default PaymentDetails;
